import { useState, useEffect } from 'react';
import {
  UIBox,
  UITypography,
  UICard,
  UICardContent,
  UIButton,
  UIAlertDestructive,
  UIBadge,
  UIAvatar,
  UIAvatarFallback,
  UISkeleton,
} from '@workspace/ui/composed';
import { AppWindow } from 'lucide-react';
import { api } from '../utils/api-client';
import { useTranslations } from '../i18n/useTranslations';

interface Application {
  clientId: string;
  clientName?: string;
  description?: string;
  inUse?: boolean;
  offlineAccess?: boolean;
  consent?: {
    grantedScopes?: string[];
  };
}

export default function ApplicationsPage() {
  const { t } = useTranslations('pages.applications');
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getApplications();
      setApplications(data);
    } catch (err) {
      setError(t('loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConsent = async (clientId: string) => {
    try {
      await api.deleteConsent(clientId);
      await loadApplications();
    } catch (err) {
      setError(t('revokeError'));
      console.error(err);
    }
  };

  if (loading) {
    return (
      <UIBox className="space-y-6">
        {/* Page Header Skeleton */}
        <UISkeleton className="h-7 w-40" />

        {/* Applications List Skeleton */}
        <UICard>
          <UICardContent className="p-6">
            <UIBox className="divide-y divide-border">
              {[1, 2, 3].map((i) => (
                <UIBox key={i} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                  <UISkeleton className="h-10 w-10 rounded-full shrink-0" />
                  <UIBox className="flex-1 min-w-0 space-y-2">
                    <UISkeleton className="h-5 w-48" />
                    <UISkeleton className="h-4 w-64" />
                    <UIBox className="flex gap-1.5 mt-2">
                      <UISkeleton className="h-5 w-16" />
                      <UISkeleton className="h-5 w-20" />
                      <UISkeleton className="h-5 w-14" />
                    </UIBox>
                  </UIBox>
                </UIBox>
              ))}
            </UIBox>
          </UICardContent>
        </UICard>
      </UIBox>
    );
  }

  return (
    <UIBox className="space-y-6">
      <UITypography variant="h4" className="font-semibold">
        {t('title')}
      </UITypography>

      {error && (
        <UIAlertDestructive>
          {error}
        </UIAlertDestructive>
      )}

      <UICard>
        <UICardContent className="p-6">
          {applications.length === 0 ? (
            <UIBox className="flex flex-col items-center justify-center py-12">
              <AppWindow className="h-12 w-12 text-muted-foreground mb-4" />
              <UITypography variant="p" className="text-muted-foreground text-center">
                {t('noApplications')}
              </UITypography>
            </UIBox>
          ) : (
            <UIBox className="divide-y divide-border">
              {applications.map((app) => (
                <UIBox
                  key={app.clientId}
                  className="flex gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <UIAvatar size="md" variant="default" className="shrink-0">
                    <UIAvatarFallback className="bg-primary text-primary-foreground">
                      <AppWindow className="h-5 w-5" />
                    </UIAvatarFallback>
                  </UIAvatar>
                  <UIBox className="flex-1 min-w-0">
                    <UIBox className="flex items-center gap-2 flex-wrap">
                      <UITypography variant="p" className="font-medium">
                        {app.clientName || app.clientId}
                      </UITypography>
                      {app.inUse && (
                        <UIBadge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
                          {t('inUse')}
                        </UIBadge>
                      )}
                      {app.offlineAccess && (
                        <UIBadge variant="outline">
                          {t('offlineAccess')}
                        </UIBadge>
                      )}
                    </UIBox>
                    {app.description && (
                      <UITypography variant="muted" className="text-sm mt-1">
                        {app.description}
                      </UITypography>
                    )}
                    {app.consent?.grantedScopes && app.consent.grantedScopes.length > 0 && (
                      <UIBox className="mt-3">
                        <UITypography variant="muted" className="text-xs mb-2">
                          {t('grantedPermissions')}
                        </UITypography>
                        <UIBox className="flex gap-1.5 flex-wrap">
                          {app.consent.grantedScopes.map((scope) => (
                            <UIBadge
                              key={scope}
                              variant="outline"
                              className="text-xs"
                            >
                              {scope}
                            </UIBadge>
                          ))}
                        </UIBox>
                      </UIBox>
                    )}
                    {app.consent && (
                      <UIBox className="mt-3">
                        <UIButton
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-auto px-2 py-1"
                          onClick={() => handleRevokeConsent(app.clientId)}
                        >
                          {t('revokeConsent')}
                        </UIButton>
                      </UIBox>
                    )}
                  </UIBox>
                </UIBox>
              ))}
            </UIBox>
          )}
        </UICardContent>
      </UICard>
    </UIBox>
  );
}
